import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RetentionPurgeForm — Sub-sprint D-β-β.
 *
 * Generic two-mode T3 retention purge form. Mode wired by prop:
 *   - "qr_pre_lookup_denials" → POST /admin/qr-tokens/_ops/pre-lookup-denials-retention-purge
 *     params: { retention_days [30,365], dry_run, confirm, reason }
 *   - "mock_outbox" → POST /admin/notifications/_ops/mock-outbox-retention-purge
 *     body: { older_than_days [1,730], confirm, reason }
 *
 * Dry-run first (always); real-run requires confirm + reason (min 3).
 */
const CONFIG = {
  qr_pre_lookup_denials: {
    titleKey: "ops.retention.qr_title",
    collection: "qr_resolve_pre_lookup_denials",
    endpoint: "/admin/qr-tokens/_ops/pre-lookup-denials-retention-purge",
    daysField: "retention_days",
    daysMin: 30, daysMax: 365, daysDefault: 90,
    mode: "query",
  },
  mock_outbox: {
    titleKey: "ops.retention.mock_title",
    collection: "notifications_mock_outbox",
    endpoint: "/admin/notifications/_ops/mock-outbox-retention-purge",
    daysField: "older_than_days",
    daysMin: 1, daysMax: 730, daysDefault: 90,
    mode: "body",
  },
};

export const RetentionPurgeForm = ({ kind, dataTestId }) => {
  const t = useRacketLabAdminT();
  const cfg = CONFIG[kind];
  const tid = dataTestId || `rl-ops-retention-${kind === "qr_pre_lookup_denials" ? "qr" : "mock-outbox"}`;
  const [days, setDays] = useState(cfg.daysDefault);
  const [reason, setReason] = useState("");
  const [dryRunResult, setDryRunResult] = useState(null);
  const [realResult, setRealResult] = useState(null);
  const [err, setErr] = useState(null);
  const { run, loading } = useAdminMutation();

  const reasonOk = reason.trim().length >= 3;
  const daysOk = days >= cfg.daysMin && days <= cfg.daysMax;

  const doDryRun = async () => {
    setErr(null); setDryRunResult(null); setRealResult(null);
    try {
      let r;
      if (cfg.mode === "query") {
        r = await run("post", `${cfg.endpoint}?${cfg.daysField}=${days}&dry_run=true`, {});
      } else {
        r = await run("post", cfg.endpoint, { [cfg.daysField]: days, confirm: false });
      }
      setDryRunResult(r);
    } catch (e) { setErr({ status: e?.status, detail: e?.detail || t("ops.common.error_unknown") }); }
  };

  const doReal = async () => {
    setErr(null); setRealResult(null);
    try {
      let r;
      if (cfg.mode === "query") {
        r = await run("post", `${cfg.endpoint}?${cfg.daysField}=${days}&dry_run=false&confirm=true&reason=${encodeURIComponent(reason.trim())}`, {});
      } else {
        r = await run("post", cfg.endpoint, { [cfg.daysField]: days, confirm: true, reason: reason.trim() });
      }
      setRealResult(r);
    } catch (e) { setErr({ status: e?.status, detail: e?.detail || t("ops.common.error_unknown") }); }
  };

  return (
    <section data-testid={`${tid}-form`} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4">
      <header>
        <h2 className="text-sm font-medium text-slate-800">{t(cfg.titleKey)}</h2>
        <p className="text-[11px] text-slate-500">
          {/* Splits warning_affects_only at {collection} so the code tag
              wraps the raw collection name while the surrounding copy is
              translated. NOT-reversible suffix remains rose-emphasised. */}
          {(() => {
            const tpl = t("ops.retention.warning_affects_only", { collection: "__COLL__" });
            const [pre, rest = ""] = tpl.split("__COLL__");
            return (
              <>
                {pre}<code className="font-mono">{cfg.collection}</code>{rest}
              </>
            );
          })()}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.retention.days_field_range_label", { field: cfg.daysField, min: cfg.daysMin, max: cfg.daysMax })}</span>
          <input
            type="number"
            min={cfg.daysMin}
            max={cfg.daysMax}
            value={days}
            onChange={(e) => setDays(Math.max(cfg.daysMin, Math.min(cfg.daysMax, Number(e.target.value) || cfg.daysMin)))}
            disabled={loading}
            data-testid={`${tid}-days`}
            className="w-32 rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          />
        </label>
        <button
          type="button"
          data-testid={`${tid}-dry-btn`}
          onClick={doDryRun}
          disabled={loading || !daysOk}
          className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${loading || !daysOk ? "bg-sky-300 cursor-not-allowed" : "bg-sky-600 hover:bg-sky-700"}`}
        >
          {t("ops.retention.dry_run_btn")}
        </button>
      </div>

      {dryRunResult && (
        <div data-testid={`${tid}-dry-result`} className="rounded-md border border-sky-200 bg-sky-50 p-3 text-[11px]">
          <p className="mb-2 font-medium text-sky-900">{t("ops.retention.dry_run_result_title")}</p>
          <JSONInspector value={dryRunResult} dataTestId={`${tid}-dry-json`} />
        </div>
      )}

      {dryRunResult && (
        <div data-testid={`${tid}-irreversible-banner`} className="rounded-md border border-rose-300 bg-rose-50 p-3 text-[11px] text-rose-900">
          <p className="inline-flex items-center gap-1 font-medium">
            <AlertTriangle className="w-3.5 h-3.5" /> {t("ops.retention.irreversible_title")}
          </p>
          <p className="mt-1">
            {(() => {
              const tpl = t("ops.retention.warning_real_purge", { collection: "__COLL__" });
              const [pre, rest = ""] = tpl.split("__COLL__");
              return (<><span>{pre}</span><code className="font-mono">{cfg.collection}</code><span>{rest}</span></>);
            })()}
          </p>
          <label className="mt-2 flex flex-col gap-1">
            <span className="font-medium">{t("ops.retention.reason_label_min3")}</span>
            <textarea
              data-testid={`${tid}-reason`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={2}
              className={`rounded-md border bg-white px-2 py-1 ${reasonOk ? "border-slate-300" : "border-rose-300"}`}
            />
          </label>
          <button
            type="button"
            data-testid={`${tid}-real-btn`}
            onClick={doReal}
            disabled={loading || !reasonOk}
            className={`mt-2 rounded-md px-3 py-1.5 text-xs font-medium text-white ${loading || !reasonOk ? "bg-rose-300 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700"}`}
          >
            {t("ops.retention.apply_real_btn")}
          </button>
        </div>
      )}

      {realResult && (
        <div data-testid={`${tid}-real-result`} className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[11px]">
          <p className="mb-2 font-medium text-emerald-900">{t("ops.retention.purged_line", { count: realResult.deleted ?? realResult.deleted_count ?? 0 })}</p>
          <JSONInspector value={realResult} dataTestId={`${tid}-real-json`} />
        </div>
      )}

      {err && (
        <div data-testid={`${tid}-error`} className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {err.status || ""}: {err.detail || t("ops.common.error_unknown")}
        </div>
      )}
    </section>
  );
};

export default RetentionPurgeForm;
